import { useState, useEffect, useMemo } from 'react';
import { BusinessCardCustomization, BusinessCardLocationData } from '../types/cart';
import { BusinessCardCustomizerProps } from '../types/calendar';
import { BusinessCardForm } from './business-card/BusinessCardForm';
import { BusinessCardPreview } from './business-card/BusinessCardPreview';
import { CardTemplatePreview } from './business-card/CardTemplatePreview';
import type { CardTemplateData, CardTemplateElement } from '../types/cardTemplate';

const PHONE_FIELDS = ['phone', 'cell_phone', 'fax', 'customer_service_phone', 'toll_free'];

function applyPhoneMask(value: string, mask: string): string {
  const digits = value.replace(/\D/g, '');
  const maxDigits = (mask.match(/9/g) ?? []).length;
  const limited = digits.slice(0, maxDigits);
  let result = '';
  let digitIndex = 0;
  for (const char of mask) {
    if (digitIndex >= limited.length) break;
    if (char === '9') result += limited[digitIndex++];
    else result += char;
  }
  return result;
}

async function fetchCardTemplate(templateId: number): Promise<CardTemplateData> {
  const response = await fetch(`/api/v2/card-template/${templateId}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch card template ${templateId}`);
  return response.json();
}

export function BusinessCardCustomizer({
  onCustomizationChange,
  initialCustomization,
  productImages,
  previewImage,
  withBorder = true,
  style,
  fields = [],
  options,
  useCardBuilder = false,
  cardTemplateId = null,
}: BusinessCardCustomizerProps) {
  const cardClass = withBorder ? 'bg-white rounded-xl shadow-lg border border-gray-200 p-6' : '';
  const [data, setData] = useState<BusinessCardLocationData>(initialCustomization || {});
  const [cardTemplate, setCardTemplate] = useState<CardTemplateData | null>(null);
  const [templateLoadError, setTemplateLoadError] = useState(false);

  const isCardBuilderMode = useCardBuilder && cardTemplateId != null;

  useEffect(() => {
    if (!useCardBuilder || !cardTemplateId) return;
    let cancelled = false;
    const run = async () => {
      await Promise.resolve();
      if (cancelled) return;
      setTemplateLoadError(false);
      try {
        const template = await fetchCardTemplate(cardTemplateId);
        if (!cancelled) setCardTemplate(template);
      } catch {
        if (!cancelled) setTemplateLoadError(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [useCardBuilder, cardTemplateId]);

  // Field elements from the card template — used for the form and preview
  const templateFieldElements: CardTemplateElement[] = useMemo(
    () => cardTemplate ? cardTemplate.elements.filter(el => el.type === 'field') : [],
    [cardTemplate],
  );

  // When the template loads and initial data has positional keys (0, 1, 2...)
  // instead of element ID keys (9000, 9001...), remap values by position.
  // This happens when PHP serialises sequential-int-keyed objects as JSON arrays.
  useEffect(() => {
    if (templateFieldElements.length === 0) return;
    if (Object.keys(data).length === 0) return;
    const templateIds = new Set(templateFieldElements.map(el => String(el.id)));
    const hasMatchingKeys = Object.keys(data).some(key => templateIds.has(key));
    if (!hasMatchingKeys) {
      const positionalValues = Object.values(data);
      const remapped: BusinessCardLocationData = {};
      templateFieldElements.forEach((element, index) => {
        if (index < positionalValues.length) {
          remapped[String(element.id)] = positionalValues[index];
        }
      });
      setData(remapped);
      onCustomizationChange(remapped as BusinessCardCustomization);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateFieldElements]);

  const cardBuilderPhoneMasks = useMemo(
    () => new Map(
      templateFieldElements
        .filter(el => (el.data as { phone?: { mask: string } | null }).phone != null)
        .map(el => [String(el.id), (el.data as { phone: { mask: string } }).phone.mask])
    ),
    [templateFieldElements],
  );

  const cardBuilderMaxChars = useMemo(
    () => new Map(
      templateFieldElements
        .filter(el => (el.data as { maxChars?: unknown }).maxChars != null)
        .map(el => [String(el.id), (el.data as { maxChars: number }).maxChars])
    ),
    [templateFieldElements],
  );

  const handleFieldChange = (field: string, value: string) => {
    const phoneMask = isCardBuilderMode
      ? cardBuilderPhoneMasks.get(field)
      : PHONE_FIELDS.includes(field) ? '999-999-9999' : undefined;

    let processedValue = phoneMask ? applyPhoneMask(value, phoneMask) : value;

    if (isCardBuilderMode && !phoneMask) {
      const maxChars = cardBuilderMaxChars.get(field);
      if (maxChars != null && processedValue.length > maxChars) {
        processedValue = processedValue.slice(0, maxChars);
      }
    }

    const updated = { ...data, [field]: processedValue };
    setData(updated);
    onCustomizationChange(updated as BusinessCardCustomization);
  };

  const fontColor = options?.color || undefined;

  const cardBuilderFormFields = templateFieldElements.map(el => ({
    key:   String(el.id),
    label: (el.data.placeholder as string | undefined) || `Field ${el.id}`,
  }));

  return (
    <div className="space-y-8">
      {options?.text_message && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          {options.text_message}
        </div>
      )}

      <div className={cardClass}>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Print Preview</h3>

        {isCardBuilderMode ? (
          templateLoadError ? (
            <p className="text-sm text-red-600">Failed to load card template. Please refresh and try again.</p>
          ) : !cardTemplate ? (
            <p className="text-sm text-gray-500">Loading template…</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <BusinessCardForm
                data={data}
                onFieldChange={handleFieldChange}
                fields={cardBuilderFormFields}
              />
              <CardTemplatePreview
                template={cardTemplate}
                fieldValues={data as Record<string, string>}
              />
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BusinessCardForm data={data} onFieldChange={handleFieldChange} fields={fields} />
            <BusinessCardPreview
              productImages={productImages}
              previewImage={previewImage}
              data={data}
              style={style}
              fields={fields}
              fontColor={fontColor}
            />
          </div>
        )}
      </div>
    </div>
  );
}
